import React, { useState, useEffect, useCallback } from 'react';
import { Search, CheckCircle, XCircle, Plus, X, Eye, Clock, AlertTriangle, Building2, Mail, Globe, Phone, MapPin, ArrowUpDown } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


// ─── Add Partner Modal ────────────────────────────────────────────────────────
function AddPartnerModal({ onClose, onSave }) {
  const [form, setForm] = useState({ business_name: '', contact_email: '', website_url: '', partner_tier: 'basic' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { error: err } = await supabase?.from('partner_profiles')?.insert({
        id: (await supabase?.auth?.getUser())?.data?.user?.id,
        email: form?.contact_email,
        business_name: form?.business_name,
        contact_email: form?.contact_email,
        website_url: form?.website_url,
        partner_tier: form?.partner_tier,
        is_active: true,
        api_key: Math.random()?.toString(36)?.substring(2),
      });
      if (err) { setError(err?.message); return; }
      onSave();
      onClose();
    } catch (e) {
      setError('Failed to add partner.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Add New Partner</h3>
          <button onClick={onClose} style={{ color: '#64748B' }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[{ key: 'business_name', label: 'Business Name', type: 'text', placeholder: 'Company name' },
            { key: 'contact_email', label: 'Contact Email', type: 'email', placeholder: 'contact@company.com' },
            { key: 'website_url', label: 'Website URL', type: 'url', placeholder: 'https://company.com' }]?.map(field => (
            <div key={field?.key}>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{field?.label}</label>
              <input type={field?.type} value={form?.[field?.key]} onChange={e => setForm(f => ({ ...f, [field?.key]: e?.target?.value }))} placeholder={field?.placeholder} required
                className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }} />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Partner Tier</label>
            <select value={form?.partner_tier} onChange={e => setForm(f => ({ ...f, partner_tier: e?.target?.value }))}
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
              <option value="basic">Basic</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          {error && <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>}
          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
            {loading ? 'Adding...' : 'Add Partner'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Application Detail Modal ─────────────────────────────────────────────────
function ApplicationDetailModal({ application, onClose, onAction }) {
  const [view, setView] = useState('detail'); // 'detail' | 'approve' | 'reject'
  const [approveTier, setApproveTier] = useState(application?.preferred_tier || 'pro');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const REJECT_REASONS = [
    'Not Eligible', 'Duplicate Application', 'Insufficient Information',
    'Business Verification Failed', 'Other'
  ];

  const refNum = application?.id?.substring(0, 8)?.toUpperCase();

  const handleApprove = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data, error: fnErr } = await supabase?.functions?.invoke('approve-partner', {
        body: {
          action: 'approve',
          application_id: application?.id,
          company_name: application?.company_name,
          contact_first_name: application?.contact_first_name,
          contact_last_name: application?.contact_last_name,
          contact_email: application?.contact_email,
          preferred_tier: approveTier,
          reviewed_by: user?.id,
        },
      });
      if (fnErr || data?.error) {
        setError(fnErr?.message || data?.error || 'Approval failed.');
        return;
      }
      onAction('approved');
      onClose();
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason) { setError('Please select a rejection reason.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data, error: fnErr } = await supabase?.functions?.invoke('approve-partner', {
        body: {
          action: 'reject',
          application_id: application?.id,
          company_name: application?.company_name,
          contact_first_name: application?.contact_first_name,
          contact_email: application?.contact_email,
          rejection_reason: rejectReason,
          rejection_notes: rejectNotes,
          reviewed_by: user?.id,
        },
      });
      if (fnErr || data?.error) {
        setError(fnErr?.message || data?.error || 'Rejection failed.');
        return;
      }
      onAction('rejected');
      onClose();
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tierColors = { basic: '#10B981', pro: '#00E5FF', enterprise: '#7B2FBE' };
  const tierColor = tierColors?.[application?.preferred_tier] || '#00E5FF';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" style={{ background: 'rgba(13,27,42,0.97)', border: '1px solid rgba(0,229,255,0.2)', backdropFilter: 'blur(20px)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'rgba(0,229,255,0.1)' }}>
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{application?.company_name}</h3>
            <p className="text-xs mt-0.5" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Ref: #{refNum} · Submitted {new Date(application?.submitted_at)?.toLocaleDateString()}</p>
          </div>
          <button onClick={onClose} style={{ color: '#64748B' }}><X size={20} /></button>
        </div>

        <div className="p-6">
          {/* Detail View */}
          {view === 'detail' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {/* Business Info */}
                <div className="rounded-xl p-4" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.08)' }}>
                  <p className="text-xs font-semibold mb-3" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>BUSINESS INFO</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Building2 size={13} style={{ color: '#64748B' }} />
                      <span className="text-xs text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{application?.industry} · {application?.business_type}</span>
                    </div>
                    {application?.website && (
                      <div className="flex items-center gap-2">
                        <Globe size={13} style={{ color: '#64748B' }} />
                        <a href={application?.website} target="_blank" rel="noreferrer" className="text-xs" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{application?.website}</a>
                      </div>
                    )}
                    {application?.description && (
                      <p className="text-xs mt-2" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6 }}>{application?.description}</p>
                    )}
                  </div>
                </div>
                {/* Contact Info */}
                <div className="rounded-xl p-4" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.08)' }}>
                  <p className="text-xs font-semibold mb-3" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>CONTACT</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Mail size={13} style={{ color: '#64748B' }} />
                      <span className="text-xs text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{application?.contact_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{application?.contact_first_name} {application?.contact_last_name}</span>
                    </div>
                    {application?.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={13} style={{ color: '#64748B' }} />
                        <span className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{application?.phone}</span>
                      </div>
                    )}
                    {(application?.city || application?.country) && (
                      <div className="flex items-center gap-2">
                        <MapPin size={13} style={{ color: '#64748B' }} />
                        <span className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{[application?.city, application?.country]?.filter(Boolean)?.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Partnership Details */}
              <div className="rounded-xl p-4" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.08)' }}>
                <p className="text-xs font-semibold mb-3" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>PARTNERSHIP DETAILS</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs mb-1" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Preferred Tier</p>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: `${tierColor}18`, color: tierColor, border: `1px solid ${tierColor}33`, fontFamily: 'DM Sans, sans-serif' }}>
                      {application?.preferred_tier?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Expected Scans</p>
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{application?.expected_scans || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Referral Source</p>
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{application?.referral_source || '—'}</p>
                  </div>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setView('approve'); setError(''); }}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #00B4CC, #00E5FF)', fontFamily: 'DM Sans, sans-serif' }}>
                  <CheckCircle size={15} /> Approve
                </button>
                <button onClick={() => { setView('reject'); setError(''); }}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>
                  <XCircle size={15} /> Reject
                </button>
              </div>
            </div>
          )}

          {/* Approve Confirmation */}
          {view === 'approve' && (
            <div className="space-y-5">
              <div className="rounded-xl p-4" style={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.15)' }}>
                <p className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>Confirm Approval</p>
                <p className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>A Supabase Auth account will be created and login credentials emailed to {application?.contact_email}.</p>
              </div>
              <div>
                <label className="block text-xs font-medium mb-2" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Partner Tier (can change before approving)</label>
                <div className="grid grid-cols-3 gap-2">
                  {['basic', 'pro', 'enterprise']?.map(tier => (
                    <button key={tier} type="button" onClick={() => setApproveTier(tier)}
                      className="py-2.5 rounded-xl text-xs font-semibold capitalize transition-all"
                      style={{
                        background: approveTier === tier ? `${tierColors?.[tier]}18` : 'rgba(0,0,0,0.3)',
                        border: approveTier === tier ? `1px solid ${tierColors?.[tier]}44` : '1px solid rgba(255,255,255,0.06)',
                        color: approveTier === tier ? tierColors?.[tier] : '#64748B',
                        fontFamily: 'DM Sans, sans-serif',
                      }}>
                      {tier}
                    </button>
                  ))}
                </div>
              </div>
              {error && <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => { setView('detail'); setError(''); }}
                  className="flex-1 py-3 rounded-xl text-sm font-medium"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
                  Cancel
                </button>
                <button onClick={handleApprove} disabled={loading}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: loading ? 'rgba(0,229,255,0.3)' : 'linear-gradient(135deg, #00B4CC, #00E5FF)', fontFamily: 'DM Sans, sans-serif', opacity: loading ? 0.7 : 1 }}>
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle size={15} />}
                  {loading ? 'Approving...' : 'Confirm Approval'}
                </button>
              </div>
            </div>
          )}

          {/* Reject Confirmation */}
          {view === 'reject' && (
            <div className="space-y-4">
              <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
                <p className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>Reject Application</p>
                <p className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>A rejection email will be sent to {application?.contact_email}.</p>
              </div>
              <div>
                <label className="block text-xs font-medium mb-2" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Rejection Reason <span style={{ color: '#EF4444' }}>*</span></label>
                <div className="space-y-1.5">
                  {['Not Eligible', 'Duplicate Application', 'Insufficient Information', 'Business Verification Failed', 'Other']?.map(reason => (
                    <button key={reason} type="button" onClick={() => setRejectReason(reason)}
                      className="w-full py-2.5 px-3 rounded-xl text-xs font-medium text-left transition-all"
                      style={{
                        background: rejectReason === reason ? 'rgba(239,68,68,0.1)' : 'rgba(0,0,0,0.2)',
                        border: rejectReason === reason ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(255,255,255,0.06)',
                        color: rejectReason === reason ? '#EF4444' : '#94A3B8',
                        fontFamily: 'DM Sans, sans-serif',
                      }}>
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Additional Notes (optional)</label>
                <textarea value={rejectNotes} onChange={e => setRejectNotes(e?.target?.value)} placeholder="Any additional context for the applicant..."
                  rows={3} className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-slate-600 outline-none resize-none"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }} />
              </div>
              {error && <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => { setView('detail'); setError(''); }}
                  className="flex-1 py-3 rounded-xl text-sm font-medium"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
                  Cancel
                </button>
                <button onClick={handleReject} disabled={loading}
                  className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                  style={{ background: loading ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', fontFamily: 'DM Sans, sans-serif', opacity: loading ? 0.7 : 1 }}>
                  {loading ? <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> : <XCircle size={15} />}
                  {loading ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Applications Tab ─────────────────────────────────────────────────────────
function ApplicationsTab({ onPendingCountChange }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [metrics, setMetrics] = useState({ total: 0, pending: 0, approvedThisMonth: 0, rejectionRate: 0 });
  const [actionMsg, setActionMsg] = useState('');

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('partner_applications')?.select('*');
      if (search) query = query?.or(`company_name.ilike.%${search}%,contact_email.ilike.%${search}%`);
      if (filterTier) query = query?.eq('preferred_tier', filterTier);
      if (filterIndustry) query = query?.eq('industry', filterIndustry);
      query = query?.order('submitted_at', { ascending: sortAsc });
      const { data, error } = await query;
      if (!error) {
        setApplications(data || []);
        // Compute metrics
        const all = data || [];
        const pending = all?.filter(a => a?.application_status === 'pending')?.length;
        const now = new Date();
        const approvedThisMonth = all?.filter(a => {
          if (a?.application_status !== 'approved') return false;
          const d = new Date(a.reviewed_at);
          return d?.getMonth() === now?.getMonth() && d?.getFullYear() === now?.getFullYear();
        })?.length;
        const rejected = all?.filter(a => a?.application_status === 'rejected')?.length;
        const reviewed = all?.filter(a => a?.application_status !== 'pending')?.length;
        const rejectionRate = reviewed > 0 ? Math.round((rejected / reviewed) * 100) : 0;
        setMetrics({ total: all?.length, pending, approvedThisMonth, rejectionRate });
        onPendingCountChange?.(pending);
      }
    } catch (err) {
      console.error('Load applications error:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterTier, filterIndustry, sortAsc, onPendingCountChange]);

  useEffect(() => { loadApplications(); }, [loadApplications]);

  useEffect(() => {
    const channel = supabase?.channel('partner_applications_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'partner_applications' }, () => {
        loadApplications();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [loadApplications]);

  const getDaysWaiting = (submittedAt) => {
    const diff = (Date.now() - new Date(submittedAt)?.getTime()) / (1000 * 60 * 60 * 24);
    return Math.floor(diff);
  };

  const getDaysColor = (days) => {
    if (days < 2) return '#10B981';
    if (days <= 5) return '#F59E0B';
    return '#EF4444';
  };

  const tierColors = { basic: '#10B981', pro: '#00E5FF', enterprise: '#7B2FBE' };
  const statusColors = { pending: '#F59E0B', approved: '#10B981', rejected: '#EF4444' };

  const pendingApps = applications?.filter(a => a?.application_status === 'pending');

  return (
    <div className="space-y-5">
      {/* Metrics Bar */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Applications', value: metrics?.total, icon: Building2, color: '#00E5FF' },
          { label: 'Pending Review', value: metrics?.pending, icon: Clock, color: '#F59E0B' },
          { label: 'Approved This Month', value: metrics?.approvedThisMonth, icon: CheckCircle, color: '#10B981' },
          { label: 'Rejection Rate', value: `${metrics?.rejectionRate}%`, icon: AlertTriangle, color: '#EF4444' },
        ]?.map(stat => {
          const Icon = stat?.icon;
          return (
            <div key={stat?.label} className="rounded-xl p-4" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.08)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color: stat?.color }} />
                <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{stat?.label}</p>
              </div>
              <p className="text-2xl font-bold" style={{ color: stat?.color, fontFamily: 'DM Sans, sans-serif' }}>{stat?.value}</p>
            </div>
          );
        })}
      </div>
      {actionMsg && (
        <div className="px-4 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{actionMsg}</div>
      )}
      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
          <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search company or email..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }} />
        </div>
        <select value={filterTier} onChange={e => setFilterTier(e?.target?.value)}
          className="px-3 py-2.5 rounded-xl text-sm outline-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: filterTier ? '#E2E8F0' : '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
          <option value="" style={{ background: '#0D1B2A' }}>All Tiers</option>
          <option value="basic" style={{ background: '#0D1B2A' }}>Basic</option>
          <option value="pro" style={{ background: '#0D1B2A' }}>Pro</option>
          <option value="enterprise" style={{ background: '#0D1B2A' }}>Enterprise</option>
        </select>
        <select value={filterIndustry} onChange={e => setFilterIndustry(e?.target?.value)}
          className="px-3 py-2.5 rounded-xl text-sm outline-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: filterIndustry ? '#E2E8F0' : '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
          <option value="" style={{ background: '#0D1B2A' }}>All Industries</option>
          {['Retail', 'Electronics', 'Groceries', 'Fashion', 'Health & Beauty', 'Sports', 'Home & Living', 'Other']?.map(i => (
            <option key={i} value={i} style={{ background: '#0D1B2A' }}>{i}</option>
          ))}
        </select>
        <button onClick={() => setSortAsc(s => !s)}
          className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-medium"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>
          <ArrowUpDown size={13} /> {sortAsc ? 'Oldest First' : 'Newest First'}
        </button>
      </div>
      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,255,0.1)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.4)' }}>
                {['Company', 'Industry', 'Tier', 'Contact Email', 'Expected Scans', 'Submitted', 'Days Waiting', 'Status', 'Actions']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading applications...</td></tr>
              ) : applications?.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No applications found</td></tr>
              ) : applications?.map((app, i) => {
                const days = getDaysWaiting(app?.submitted_at);
                const daysColor = app?.application_status === 'pending' ? getDaysColor(days) : '#64748B';
                const tColor = tierColors?.[app?.preferred_tier] || '#94A3B8';
                const sColor = statusColors?.[app?.application_status] || '#94A3B8';
                return (
                  <tr key={app?.id} className="cursor-pointer transition-colors"
                    onClick={() => app?.application_status === 'pending' && setSelectedApp(app)}
                    style={{ borderTop: '1px solid rgba(0,229,255,0.06)', background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #7B2FBE33, #00E5FF33)' }}>
                          {(app?.company_name || 'A')?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{app?.company_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{app?.industry}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold capitalize px-2 py-0.5 rounded-full" style={{ background: `${tColor}18`, color: tColor, fontFamily: 'DM Sans, sans-serif' }}>{app?.preferred_tier}</span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{app?.contact_email}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{app?.expected_scans || '—'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{app?.submitted_at ? new Date(app?.submitted_at)?.toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold" style={{ color: daysColor, fontFamily: 'DM Sans, sans-serif' }}>
                        {app?.application_status === 'pending' ? `${days}d` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full capitalize" style={{ background: `${sColor}18`, color: sColor, fontFamily: 'DM Sans, sans-serif' }}>{app?.application_status}</span>
                    </td>
                    <td className="px-4 py-3">
                      {app?.application_status === 'pending' && (
                        <button onClick={e => { e?.stopPropagation(); setSelectedApp(app); }}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium"
                          style={{ background: 'rgba(0,229,255,0.1)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.2)', fontFamily: 'DM Sans, sans-serif' }}>
                          <Eye size={12} /> Review
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {selectedApp && (
        <ApplicationDetailModal
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onAction={(result) => {
            setActionMsg(`Application ${result} successfully.`);
            setTimeout(() => setActionMsg(''), 4000);
            loadApplications();
          }}
        />
      )}
    </div>
  );
}

// ─── Main PartnerManagement ───────────────────────────────────────────────────
export default function PartnerManagement() {
  const [activeTab, setActiveTab] = useState('active');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('partner_profiles')?.select('*, retail_products(id)', { count: 'exact' });
      if (search) query = query?.ilike('business_name', `%${search}%`);
      if (activeTab === 'active') query = query?.eq('is_active', true);
      if (activeTab === 'suspended') query = query?.eq('is_active', false);
      const { data, error } = await query?.order('created_at', { ascending: false });
      if (!error) setPartners(data || []);
    } catch (err) {
      console.error('Load partners error:', err);
    } finally {
      setLoading(false);
    }
  }, [search, activeTab]);

  useEffect(() => {
    if (activeTab !== 'applications') loadPartners();
  }, [loadPartners, activeTab]);

  useEffect(() => {
    const channel = supabase?.channel('admin_partners_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'partner_profiles' }, () => {
        if (activeTab !== 'applications') loadPartners();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [loadPartners, activeTab]);

  const handleToggleActive = async (partner) => {
    try {
      await supabase?.from('partner_profiles')?.update({ is_active: !partner?.is_active })?.eq('id', partner?.id);
      setActionMsg(`Partner ${partner?.is_active ? 'suspended' : 'activated'}.`);
      loadPartners();
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const tierColors = { basic: '#94A3B8', silver: '#CBD5E1', gold: '#F59E0B', platinum: '#00E5FF' };

  const TABS = [
    { id: 'active', label: 'Active Partners' },
    { id: 'applications', label: 'Pending Applications', badge: pendingCount },
    { id: 'suspended', label: 'Suspended' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Partner Management</h2>
        {activeTab !== 'applications' && (
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
            <Plus size={15} /> Add Partner
          </button>
        )}
      </div>
      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.08)' }}>
        {TABS?.map(tab => (
          <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{
              background: activeTab === tab?.id ? 'linear-gradient(135deg, rgba(0,229,255,0.12), rgba(123,47,190,0.12))' : 'transparent',
              border: activeTab === tab?.id ? '1px solid rgba(0,229,255,0.2)' : '1px solid transparent',
              color: activeTab === tab?.id ? '#E2E8F0' : '#64748B',
              fontFamily: 'DM Sans, sans-serif',
            }}>
            {tab?.label}
            {tab?.badge > 0 && (
              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#EF4444', color: '#fff', fontFamily: 'DM Sans, sans-serif', minWidth: '18px', textAlign: 'center' }}>
                {tab?.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <ApplicationsTab onPendingCountChange={setPendingCount} />
      )}
      {/* Active / Suspended Partners */}
      {activeTab !== 'applications' && (
        <>
          {actionMsg && (
            <div className="px-4 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{actionMsg}</div>
          )}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
            <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search partners..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }} />
          </div>
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,255,0.1)' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.4)' }}>
                    {['Company', 'Tier', 'Products', 'Status', 'Joined', 'Actions']?.map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading partners...</td></tr>
                  ) : partners?.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No partners found</td></tr>
                  ) : partners?.map((partner, i) => (
                    <tr key={partner?.id} style={{ borderTop: '1px solid rgba(0,229,255,0.06)', background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #7B2FBE33, #00E5FF33)' }}>
                            {(partner?.business_name || 'P')?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{partner?.business_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium capitalize" style={{ color: tierColors?.[partner?.partner_tier] || '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{partner?.partner_tier || 'basic'}</span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{partner?.retail_products?.length || 0}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full" style={{ background: partner?.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: partner?.is_active ? '#10B981' : '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{partner?.is_active ? 'Active' : 'Suspended'}</span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{partner?.created_at ? new Date(partner?.created_at)?.toLocaleDateString() : 'N/A'}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleToggleActive(partner)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium"
                          style={{ background: partner?.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: partner?.is_active ? '#EF4444' : '#10B981', border: `1px solid ${partner?.is_active ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)'}`, fontFamily: 'DM Sans, sans-serif' }}>
                          {partner?.is_active ? <><XCircle size={12} /> Suspend</> : <><CheckCircle size={12} /> Activate</>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {showAdd && <AddPartnerModal onClose={() => setShowAdd(false)} onSave={loadPartners} />}
    </div>
  );
}
